// The filter builder's ALL/ANY toggle is stored internally as 'AND'/'OR' (it
// mirrors the connector labels shown between filter rows) — the backend's
// match_type field expects 'ALL'/'ANY' instead.
export function toMatchType(op) {
  return op === 'OR' ? 'ANY' : 'ALL';
}

// Matches backend's options.service.js CAMPAIGN_STATUS_LABELS — kept in sync
// manually since one's Node, one's a browser bundle.
const CAMPAIGN_STATUS_LABEL = { sent: 'Pitched', failed: 'Failed', delivered: 'Delivered' };

const PROFILE_FIELD_BY_PROP = {
  Platform: 'mobile_platform',
  City: 'location_city',
  State: 'location_state',
  Country: 'location_country',
  Language: 'language',
  'KYC Status': 'kyc_status',
  'User ID': 'display_user_id',
  Badge: 'custom_field_1',
  Source: 'custom_field_2',
};

const ENTITY_TABLE_BY_PROP = {
  'Lead Status': 'lead_states',
  'Earning Status': 'earning_states',
  'Task Status': 'task_states',
};

// Matches backend's schemaWhitelist.js EARNING_ENTITY_TYPES — real
// pickmywork-backend user_wallet_logs.entity_type values, not the
// placeholder categories the original seed data invented. Exported for
// FilterRow.jsx, which needs the raw entity_type (not the UI label) to key
// its earning condition's live property-sample lookup.
export const EARNING_TYPE_BY_PROP = {
  // 'ALL' is a sentinel queryBuilder.service.js's buildEarningCondition
  // recognizes as "don't filter by entity_type at all" — sums across every
  // earning category for that user instead of one specific one.
  'Any': 'ALL',
  // Array form — earningSync.job.js's deriveEventName() groups JOB+JOB_TASK
  // under one 'Project Earning' event_name label; a single-entity_type sum
  // filter would silently undercount anyone whose project earnings happen to
  // be tagged JOB_TASK instead of JOB. Same reasoning for Game/GAME_PARTICIPATION_ROUND.
  // queryBuilder.service.js's buildEarningCondition accepts entity_type as
  // either a string or an array (WHERE entity_type IN (...)).
  'Project': ['JOB', 'JOB_TASK'],
  'Game': ['GAME', 'GAME_PARTICIPATION_ROUND'],
  'General': 'GENERAL',
  'TL Earning': 'TL_EARNING',
  'Referral': 'REFERRAL_TRACKING',
  // TAX_DEDUCTION/TAX_REFUND are synthetic entity_types written only when a
  // bounced payout gets reimbursed (kue/payout/check_payout_status.js) —
  // grouped together same as Project/Game above.
  'Tax': ['TAX_DEDUCTION', 'TAX_REFUND'],
  // PAYOUT_DATA is actually a subentity_type value, not entity_type — but
  // buildEarningCondition checks both columns transparently, so this stays a
  // flat entry like everything else here.
  'Payout': 'PAYOUT_DATA',
};

// filter.timeWindowValue/timeWindowUnit are a free number + unit pair now
// (not a fixed preset) — this just normalizes them into the backend's shape.
function timeWindow(filter) {
  const value = Number(filter.timeWindowValue) || 30;
  const unit = filter.timeWindowUnit || 'days';
  return { value, unit };
}

const PROPERTY_OP_MAP = {
  equals: 'equals',
  'not equals': 'not_equals',
  in: 'in',
  'not in': 'not_in',
  'greater than': 'greater_than',
  'less than': 'less_than',
  'is set': 'is_set',
  'is not set': 'is_not_set',
};

// Drops incomplete property rows (no name typed yet) rather than sending a
// condition the backend would 400 on. Exported for journeyConfig.js's Event
// Trigger, which uses the exact same propFilters -> backend properties shape.
export function propFiltersToProperties(propFilters) {
  if (!propFilters?.length) return undefined;
  const properties = propFilters
    .filter((pf) => pf.name && !((pf.op === 'in' || pf.op === 'not in') && (!Array.isArray(pf.val) || pf.val.length === 0)))
    .map((pf) => {
      const operator = PROPERTY_OP_MAP[pf.op] || 'equals';
      if (operator === 'is_set' || operator === 'is_not_set') {
        return { name: pf.name, operator };
      }
      const value = operator === 'greater_than' || operator === 'less_than' ? Number(pf.val) : pf.val;
      return { name: pf.name, operator, value };
    });
  return properties.length ? properties : undefined;
}

// Translates one UI FilterRow object into a backend segment condition. Returns
// null if the filter isn't complete enough to submit yet (e.g. no event chosen).
export function filterToCondition(filter) {
  switch (filter.type) {
    // Lead is the same 'event' condition machinery, just always scoped to
    // lead_events (a dedicated button/list in the UI, see FilterRow.jsx,
    // rather than making admins find "Lead Verified" in the generic Event
    // dropdown) — table is forced rather than read from filter.eventTable so
    // it can't drift even if the events list momentarily contains stale data.
    case 'event':
    case 'lead': {
      if (!filter.event) return null;
      const table = filter.type === 'lead' ? 'lead_events' : (filter.eventTable || 'events');
      const did = filter.did !== false;
      const properties = propFiltersToProperties(filter.propFilters);
      if (!did) {
        return { type: 'event', table, event_name: filter.event, operator: 'has_not_done', time_window: timeWindow(filter), properties };
      }
      const count = Number(filter.countVal) || 1;
      if (count > 1) {
        return { type: 'event', table, event_name: filter.event, operator: 'has_done_more_than', count: count - 1, time_window: timeWindow(filter), properties };
      }
      return { type: 'event', table, event_name: filter.event, operator: 'has_done', time_window: timeWindow(filter), properties };
    }

    case 'user-property': {
      // City/State/User ID support picking several values at once (see
      // FilterRow's MULTI_VALUE_PROPS) — filter.val is an array there, a
      // plain string everywhere else. The backend's queryBuilder already
      // has in/not_in operators for exactly this (see options.service.js).
      const isMulti = Array.isArray(filter.val);
      if (!filter.prop || (isMulti ? filter.val.length === 0 : !filter.val)) return null;
      const negated = filter.op === 'not equals';
      return {
        type: 'profile',
        field: PROFILE_FIELD_BY_PROP[filter.prop],
        operator: isMulti ? (negated ? 'not_in' : 'in') : (negated ? 'not_equals' : 'equals'),
        value: filter.val,
      };
    }

    case 'entity-status': {
      if (!filter.prop || !filter.val) return null;
      return {
        type: 'entity_state',
        table: ENTITY_TABLE_BY_PROP[filter.prop],
        field: 'current_status',
        operator: filter.op === 'not equals' ? 'not_equals' : 'equals',
        value: filter.val,
      };
    }

    case 'earning': {
      if (!filter.prop || !filter.val) return null;
      return {
        type: 'earning',
        entity_type: EARNING_TYPE_BY_PROP[filter.prop],
        operator: 'sum_greater_than',
        value: Number(filter.val),
        time_window: timeWindow(filter),
        properties: propFiltersToProperties(filter.propFilters),
      };
    }

    case 'campaign': {
      if (!filter.campaignId || !filter.status) return null;
      const did = filter.did !== false;
      return { type: 'campaign', campaign_id: filter.campaignId, status: filter.status, operator: did ? 'has_done' : 'has_not_done' };
    }

    default:
      return null;
  }
}

// Drops incomplete filters rather than sending a condition the backend would 400 on.
export function filtersToConditions(filters) {
  return filters.map(filterToCondition).filter(Boolean);
}

// Plain-text, read-only rendering of a FilterRow (the shape conditionsToFilters
// produces) — for places that need to display a segment's criteria without
// pulling in FilterRow's full interactive editor (live event-property lookups,
// selects, etc.), e.g. a campaign's read-only "Sent to" summary.
// campaignsById (optional, {id: name}) resolves a readable campaign name for
// the 'campaign' case — callers outside the live segment builder (e.g.
// CampaignDetail's read-only summary) don't have FilterRow's `options.campaigns`
// list on hand, so this falls back to the raw id when omitted.
export function filterToText(filter, campaignsById = {}) {
  switch (filter.type) {
    case 'event':
    case 'lead': {
      const did = filter.did !== false;
      const count = Number(filter.countVal) || 1;
      const countPart = did && count > 1 ? ` ${filter.countOp || 'at least'} ${count}×` : '';
      const windowPart = ` in last ${filter.timeWindowValue || 30} ${filter.timeWindowUnit || 'days'}`;
      const propsPart = (filter.propFilters || [])
        .filter((pf) => pf.name)
        .map((pf) => ` where ${pf.name} ${pf.op} ${pf.val}`)
        .join(' and');
      return `${did ? 'Did' : 'Did not'} "${filter.event}"${countPart}${windowPart}${propsPart}`;
    }
    case 'user-property':
    case 'entity-status': {
      const isMulti = Array.isArray(filter.val);
      const opText = isMulti ? (filter.op === 'not equals' ? 'is none of' : 'is any of') : filter.op;
      return `${filter.prop} ${opText} ${isMulti ? filter.val.join(', ') : filter.val}`;
    }
    case 'earning': {
      const propsPart = (filter.propFilters || [])
        .filter((pf) => pf.name)
        .map((pf) => ` where ${pf.name} ${pf.op} ${pf.val}`)
        .join(' and');
      return `${filter.prop} sum ≥ ${filter.val} in last ${filter.timeWindowValue || 30} ${filter.timeWindowUnit || 'days'}${propsPart}`;
    }
    case 'campaign': {
      const did = filter.did !== false;
      const statusLabel = CAMPAIGN_STATUS_LABEL[filter.status] || filter.status;
      const campaignName = campaignsById[filter.campaignId] || filter.campaignId;
      return `${did ? 'Was' : 'Was not'} "${statusLabel}" in campaign "${campaignName}"`;
    }
    default:
      return '';
  }
}

function invert(map) {
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
}

const PROP_BY_PROFILE_FIELD = invert(PROFILE_FIELD_BY_PROP);
const PROP_BY_ENTITY_TABLE = invert(ENTITY_TABLE_BY_PROP);
const OP_LABEL_BY_BACKEND = invert(PROPERTY_OP_MAP);

// Reverse of EARNING_TYPE_BY_PROP, done explicitly rather than via invert()
// — some values there are arrays (['JOB','JOB_TASK']), and using them as a
// plain object key would rely on JS's implicit array-to-string coercion
// happening to match on both sides (fragile: order-dependent, easy to break
// by writing the array differently somewhere). Compares sorted-and-joined
// so key order never matters.
function serializeEntityType(value) {
  if (!value) return '';
  return Array.isArray(value) ? [...value].sort().join(',') : value;
}

function propByEarningType(entityType) {
  const target = serializeEntityType(entityType);
  const match = Object.entries(EARNING_TYPE_BY_PROP).find(([, value]) => serializeEntityType(value) === target);
  return match ? match[0] : '';
}

// Inverse of propFiltersToProperties — loads a backend event condition's/
// trigger's `properties` array back into editable FilterRow-shaped propFilter
// rows. `nextId` supplies unique ids the same way SegmentBuilder's own
// filter-add flow does.
export function propertiesToPropFilters(properties, nextId) {
  return (properties || []).map((p) => ({
    id: nextId(),
    name: p.name,
    op: OP_LABEL_BY_BACKEND[p.operator] || 'equals',
    val: Array.isArray(p.value) ? p.value.map(String) : (p.value !== undefined ? String(p.value) : ''),
  }));
}

// Inverse of filterToCondition — loads a saved segment's real backend
// conditions back into editable FilterRow objects. `nextId` supplies unique
// ids the same way SegmentBuilder's own filter-add flow does.
export function conditionsToFilters(conditions, nextId) {
  return conditions.map((condition) => {
    const id = nextId();
    switch (condition.type) {
      case 'event': {
        const did = condition.operator !== 'has_not_done';
        const countVal = condition.operator === 'has_done_more_than' ? String((condition.count || 0) + 1) : '1';
        return {
          id,
          // A saved lead_events condition re-opens under the dedicated Lead
          // button (see FilterRow.jsx/SegmentBuilder.jsx), not the generic
          // Event picker — mirrors how filterToCondition forces this same
          // table for the 'lead' filter type.
          type: condition.table === 'lead_events' ? 'lead' : 'event',
          event: condition.event_name,
          eventTable: condition.table,
          did,
          countOp: 'at least',
          countVal,
          timeWindowValue: String(condition.time_window?.value || 30),
          timeWindowUnit: condition.time_window?.unit || 'days',
          propFilters: propertiesToPropFilters(condition.properties, nextId),
        };
      }

      case 'profile': {
        const isMulti = condition.operator === 'in' || condition.operator === 'not_in';
        return {
          id,
          type: 'user-property',
          prop: PROP_BY_PROFILE_FIELD[condition.field] || '',
          op: (condition.operator === 'not_equals' || condition.operator === 'not_in') ? 'not equals' : 'equals',
          val: isMulti
            ? (Array.isArray(condition.value) ? condition.value.map(String) : [String(condition.value)])
            : condition.value,
        };
      }

      case 'entity_state':
        return {
          id,
          type: 'entity-status',
          prop: PROP_BY_ENTITY_TABLE[condition.table] || '',
          op: condition.operator === 'not_equals' ? 'not equals' : 'equals',
          val: condition.value,
        };

      case 'earning':
        return {
          id,
          type: 'earning',
          prop: propByEarningType(condition.entity_type),
          op: '≥',
          val: String(condition.value),
          timeWindowValue: String(condition.time_window?.value || 30),
          timeWindowUnit: condition.time_window?.unit || 'days',
          propFilters: propertiesToPropFilters(condition.properties, nextId),
        };

      case 'campaign':
        return {
          id,
          type: 'campaign',
          campaignId: condition.campaign_id,
          status: condition.status,
          did: condition.operator !== 'has_not_done',
        };

      default:
        return null;
    }
  }).filter(Boolean);
}
